import * as React from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router';
import { setBFAppData } from '@features/action.bfapp';
import { resetStore, restoreStore, setIsSaving } from '@company/features/action.company';
import ICompanyApp, { TCompanyApp } from './ICompanyApp';
import Loader from '@appComponents/Loader/Loader';
import CompanySubmenu from '@company/components/CompanySubmenu/CompanySubmenu';
import NotificationComponent from '@company/components/NotificationComponent/NotificationComponent';
import Company from '@company/Company';
import MarketPage from '@market/pages/MarketPage/MarketPage';
import { TReducers } from '@features/reducers';
import { ENHANCED_MARKET } from '@bundles/consts';

import * as companyStyles from '@company/AppStyle.module.scss';

export class CompanyApp extends React.Component<ICompanyApp, TCompanyApp> {
  state = {
    loader: true,
    isOwner: false,
    isCreator: false,
    isUSInvestor: false,
    editMode: false,
    formReset: false,
  };

  componentDidMount() {
    const investorId = this.props.info ? this.props.info.investor_id : undefined;

    this.props.setBFAppData(this.props.slug, this.props.userSignedIn, investorId).then(() => {
      if ((this.state.isOwner || this.state.isCreator) && this.isOverviewPage()) {
        this.toggleEditMode();
      }
    });
  }

  componentDidUpdate(prevProps: ICompanyApp, prevState: TCompanyApp) {
    if (this.props.companyDetails.id && this.state.loader) {
      this.setState({
        loader: false,
        isOwner: this.props.info && this.props.info.profile_id === this.props.companyDetails.profile_id,
        isCreator: this.props.info && this.props.info.profile_id === this.props.companyDetails.creator_id,
        isUSInvestor: ENHANCED_MARKET.includes(this.props.info?.country),
      });
    }
  }

  shouldComponentUpdate(nextProps: ICompanyApp) {
    if (!nextProps.companyDetails || nextProps.info === null) {
      return false;
    }

    return true;
  }

  // copied from old Company component
  toggleEditMode = () => {
    const slug = this.props.slug;
    this.setState({ editMode: !this.state.editMode }, () => {
      if (this.isDetailsPageVisible() && this.state.editMode) {
        this.props.history.push(`/companies/${slug}/overview`);
        return;
      }

      this.props.history.push(`/companies/${slug}`);
    });
  }

  // copied from old Company component
  isDetailsPageVisible = () => {
    const slug = this.props.slug;
    return [`/companies/${slug}`, `/companies/${slug}/overview`]
      .includes(this.props.history.location.pathname);
  }

  // copied from old Company component with restoreStore adding
  cancelEdit = () => {
    const slug = this.props.slug;
    this.setState({ editMode: false, formReset: true }, () => {

      if (this.isDetailsPageVisible()) {
        this.props.resetStore();
        setTimeout(() => {
          this.props.restoreStore();
        }, 0);
      }

      this.props.history.push(`/companies/${slug}`);
    });
  }

  // copied from old Company component
  displayTopBar = () => {
    if (this.state.editMode && this.props.validationCounter === 0 && this.props.formDirty) {
      return (
        <div className={companyStyles.topBar}>
          <div>
            <p>Changes have been made</p>
            <div>
              <button onClick={this.props.setIsSaving}>Save</button>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }

  isOverviewPage = () => this.props.history.location.pathname === `/companies/${this.props.slug}/overview`;

  render() {
    return (
      this.state.loader
        ? <Loader moreSpace={true} />
        : (
        <>
          <CompanySubmenu
            isOwner={this.state.isOwner}
            isCreator={this.state.isCreator}
            isUsInvestor={this.state.isUSInvestor}
            userSignedIn={this.props.userSignedIn}
            editMode={this.state.editMode}
            editFn={this.toggleEditMode}
            cancelFn={this.cancelEdit}
            companyDetails={this.props.companyDetails}
          >
            {this.displayTopBar()}
          </CompanySubmenu>
          <NotificationComponent />

          <Switch>
            <Route
              path={`/companies/:slug/market/spvs/:spv_id`}
              render={(props) => {
                return <MarketPage
                  {...props}
                />;
              }}
            />

            <Route
              exact={true}
              path={`/companies/:slug/:tab?/:subpage?/:action?`}
              render={(props) => {
                return <Company
                  {...props}
                  editMode={this.state.editMode}
                  isCreator={this.state.isCreator}
                  isOwner={this.state.isOwner}
                  isUsInvestor={this.state.isUSInvestor}
                />;
              }}
            />
          </Switch>
        </>
        )
    );
  }
}

const mapDispatchToProps = (dispatch: Function) => ({
  setBFAppData: (slug: string, userSignedIn: boolean, investorId?: number) => (
    dispatch(setBFAppData(slug, userSignedIn, investorId))
  ),
  resetStore: () => {
    dispatch(resetStore());
  },
  restoreStore: () => {
    dispatch(restoreStore());
  },
  setIsSaving: () => {
    dispatch(setIsSaving());
  },
});

const mapStateToProps = (store: TReducers) => {
  return {
    companyDetails: store.companyReducer.companyDetails,
    info: store.userInfo,
    validationCounter: store.companyReducer.validationCounter,
    formDirty: store.companyReducer.formDirty,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyApp);
